import React, { useMemo } from 'react';
import Image from 'next/image';
import Button from '@components/ui/Button';
import Heading from '@components/ui/Heading';
import YouTubeEmbed from '../Slices/MediaEmbed/YouTubeEmbed';

export interface PlpBannerProps {
  data: {
    heroImage: {
      dimensions: { width: number; height: number };
      url: string;
      alt: string;
    };
    videoId?: string;
    videoWidth?: string;
    videoHeight?: string;
    headline: string;
    tagline?: string;
    callToActions?: {
      cta_text: string;
      cta_url: string;
    }[];
  };
  locale?: string;
}

const PlpBanner = ({ data, locale }: PlpBannerProps) => {
  const {
    heroImage,
    headline,
    tagline,
    callToActions,
    videoId,
    videoWidth,
    videoHeight,
  } = data;

  const videoAspectRatio = useMemo(() => {
    if (Number(videoWidth) && Number(videoHeight)) {
      return (Number(videoHeight) / Number(videoWidth)) * 100;
    }
    return 42.19; // Default aspect ratio (matching Hero component)
  }, [videoWidth, videoHeight]);

  return (
    <section className="plpBannerContainer">
      <div className="plpBannerImageContainer">
        {heroImage?.url ? (
          <Image
            priority
            src={heroImage.url}
            alt={heroImage.alt}
            width={heroImage.dimensions.width}
            height={heroImage.dimensions.height}
            sizes="(min-width: 1088px) 1090px, (min-width: 720px) 818px, (min-width: 480px) 687px, 447px"
            style={{
              width: '100%',
              height: 'auto',
            }}
          />
        ) : (
          <YouTubeEmbed
            html={`<iframe width="100%" height="100%" src="https://www.youtube.com/embed/${videoId}?feature=oembed&enablejsapi=1&controls=0&autoplay=1&mute=1&loop=1&playsinline=1&modestbranding=1&rel=0&autohide=1&playlist=${videoId}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`}
            aspectRatio={videoAspectRatio}
            videoWrapperClassName
          />
        )}
      </div>
      <div className="plpBannerContentContainer">
        {headline && (
          <Heading
            semanticLevel="h1"
            styleLevel="1"
            className="plpBannerHeading"
          >
            {headline}
          </Heading>
        )}
        {tagline && <p>{tagline}</p>}
        <div className="plpBannerCtaContainer">
          {callToActions &&
            callToActions.length > 0 &&
            callToActions.map(
              ({ cta_text: ctaText, cta_url: ctaUrl }) =>
                ctaText &&
                ctaUrl && (
                  <Button
                    key={ctaText}
                    label={ctaText}
                    size="large"
                    variant="primary"
                    type="button"
                    href={`/${locale}${ctaUrl}`}
                    className="plpBannerCta"
                  />
                )
            )}
        </div>
      </div>
    </section>
  );
};

export default PlpBanner;
